import React from 'react';

import { Box, Button, CenteredLayout, Heading, IconButton, Logo, WorkflowHeader } from '@betterment/samba-react';
import { Helmet } from 'react-helmet';

import { Notifications } from '../../notifications/Notifications';
import { WithLayout } from '../../RetailApp/components/WithLayout';
import Footer from '../components-v2/footer/Footer';

interface LayoutProps {
  feature: string;
  size?: 'small' | 'medium' | 'large' | 'full';
  pageTitle: string | null;
  workflowTitle: string | null;
  onBack?: () => void;
  onClose?: () => void;
  onLogoClick?: () => void;
}

export const WorkflowLayout: React.FC<React.PropsWithChildren<LayoutProps>> = ({
  children,
  feature,
  workflowTitle,
  onBack,
  onClose,
  onLogoClick,
  size = 'medium',
  pageTitle,
}) => {
  const renderLogo = () => {
    if (onBack) {
      return <IconButton label="Back" name="LeftArrow" onClick={onBack} spacing="compact" />;
    }

    if (onLogoClick) {
      return (
        <Button onClick={onLogoClick} variant="ghostSecondary">
          <Logo
            type="signature"
            css={{
              height: '25px',
            }}
          />
        </Button>
      );
    }

    return (
      <Logo
        type="signature"
        css={{
          height: '25px',
        }}
      />
    );
  };

  return (
    <WithLayout parts={{ sidebar: false, footer: false }} feature={feature}>
      <Box
        css={{
          bg: 'background.surface',
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          gridTemplateAreas: `
          "header header header"
          "notifications notifications notifications"
          ". content ."
          ". footer ."
        `,
          gridColumnGap: {
            base: 'space.0',
            md: 'space.400',
          },
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Helmet titleTemplate="%s | Betterment" defaultTitle="Betterment">
          <title>{pageTitle}</title>
        </Helmet>
        <Notifications
          css={{
            gridArea: 'notifications',
            marginBottom: {
              base: 'space.200',
              md: 'space.400',
            },
          }}
        />
        <Box
          css={{
            bg: 'background.surface',
            gridArea: 'header',
            marginBottom: {
              base: 'space.200',
              md: 'space.400',
            },
            paddingX: 'space.400',
            borderBottom: '1px solid',
            borderBottomColor: 'border.weak',
            position: 'sticky',
            top: '0',
          }}
        >
          <WorkflowHeader
            css={{
              gridColumn: 2,
              px: 'space.0',
              maxWidth: '1200px',
              marginX: 'auto',
            }}
          >
            <WorkflowHeader.Leading css={{ display: 'flex', alignItems: 'center', gap: 'space.100' }}>
              {renderLogo()}
            </WorkflowHeader.Leading>
            <WorkflowHeader.Title>
              <Heading as="h2" variant="title100">
                {workflowTitle}
              </Heading>
            </WorkflowHeader.Title>
            <WorkflowHeader.Trailing>
              <IconButton label="Close" name="X" onClick={onClose} spacing="compact" />
            </WorkflowHeader.Trailing>
          </WorkflowHeader>
        </Box>
        <CenteredLayout
          css={{
            bg: 'background.surface',
            gridArea: 'content',
            marginBottom: {
              base: 'space.200',
              md: 'space.400',
            },
            paddingX: {
              base: 'space.400',
              smDown: 'space.300',
            },
            alignItems: 'unset',
          }}
          size={size}
        >
          {children}
        </CenteredLayout>
        <Box
          css={{
            gridArea: 'footer',
            paddingX: {
              base: 'space.400',
              smDown: 'space.300',
            },
            maxWidth: '1200px',
          }}
        >
          <Footer />
        </Box>
      </Box>
    </WithLayout>
  );
};
